// S-S5A-2 — React-Query hook для Sprint 5A.1 Wellness Insights (Stage 1 §7).
//
// GET /api/v1/insights/wellness — status-based (200 ready / 202 generating).
// Polling каждые 5 сек когда status='generating' AND enabled=true; останавливается
// на 'ready' OR enabled=false.
// staleTime 5 минут — cache hit для повторных открытий главной без regen.
//
// 60-сек timeout — в WellnessInsightsCard: component owns isTimedOut state,
// передаёт `enabled: !isTimedOut` сюда → react-query прекращает refetchInterval cycles.
//
// Retry — наследует global baseline (retry:1 из main.tsx); wellness не override'ит.

import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type { ApiError, WellnessInsightsResponse } from '@/api/types';

export type UseWellnessInsightsOptions = {
  enabled?: boolean;
};

export function wellnessRefetchInterval(
  query: { state: { data: WellnessInsightsResponse | undefined; status?: 'pending' | 'error' | 'success' } },
): number | false {
  // Sayat R1 M-H2: React Query сохраняет stale `data` при error → если последний
  // успешный response был 'generating' и потом запрос упал, refetchInterval по
  // одному data.status продолжал бы firing 5sec бесконечно (component timeout
  // gate сбрасывал isTimedOut при isError → enabled=true → zombie loop).
  // Gate на query.state.status==='error' останавливает interval native.
  if (query.state.status === 'error') return false;
  const data = query.state.data;
  if (data?.status === 'generating') return 5_000;
  return false;
}

export function wellnessQueryOptions(
  options: UseWellnessInsightsOptions = {},
): UseQueryOptions<WellnessInsightsResponse, AxiosError<ApiError>> {
  const { enabled = true } = options;
  return {
    queryKey: queryKeys.wellnessInsights,
    queryFn: async () => {
      const { data } = await api.get<WellnessInsightsResponse>('/insights/wellness');
      return data;
    },
    enabled,
    refetchInterval: wellnessRefetchInterval,
    refetchIntervalInBackground: false,
    staleTime: 5 * 60 * 1000,
  };
}

export function useWellnessInsights(
  options: UseWellnessInsightsOptions = {},
): UseQueryResult<WellnessInsightsResponse, AxiosError<ApiError>> {
  return useQuery(wellnessQueryOptions(options));
}
